import type { RouteLocationNormalized } from 'vue-router'
import { ROUTES } from '@dashboard/router/routeNames'
import { RectangleStackIcon } from '@heroicons/vue/24/outline'
import { shellIcons } from '@dashboard/components/icons/shellIcons'
import { useFlowStore } from '@dashboard/modules/flow/stores/flowStore'
import { useOfferStore } from '@dashboard/modules/offer/stores/offerStore'
import { useCampaignStore } from '@dashboard/modules/personalization/stores/campaignStore'
import { usePersonalizedEmailStore } from '@dashboard/modules/personalization/stores/personalizedEmailsStore'
import { getQuestionName } from '@dashboard/modules/segment/services/questionActions'
import { resolveQuestionId } from '@dashboard/modules/segment/services/questionNavigation'
import { useQuestionStore } from '@dashboard/modules/segment/stores/questionStore'
import { useSegmentGroupStore } from '@dashboard/modules/segment/stores/segmentGroupStore'
import { useConfigStore } from '@dashboard/stores/configStore'
import { useTemplateStore } from '@dashboard/modules/template/stores/templateStore'
import { useWidgetStore } from '@dashboard/modules/widget/stores/widgetStore'
import { pageName } from '@dashboard/modules/pages/services/pageNames'
import { readonly, ref, shallowRef, watch } from 'vue'

export interface RecentPage {
  routeName: string
  params: Record<string, string>
  label: string
  typeLabel: string
  timestamp: number
}

/** Look up the icon for a recent page entry from TRACKED_ROUTES config */
export function getRecentPageIcon(routeName: string) {
  return TRACKED_ROUTES[routeName]?.icon
}

/** Name of the tracked resource or page a route shows; reactive when read inside a computed. */
export function getTrackedRouteLabel(to: Pick<RouteLocationNormalized, 'name' | 'params'>): string | undefined {
  const config = typeof to.name === 'string' ? TRACKED_ROUTES[to.name] : undefined
  if (!config?.paramKey) {
    return config?.label
  }
  const resourceId = to.params[config.paramKey]
  return typeof resourceId === 'string' ? config.resolve?.(resourceId) : undefined
}

/**
 * Configuration: which routes to track and how to resolve their labels.
 *
 * Detail routes (with paramKey + resolve):
 *   Track individual resource pages (e.g., a specific flow or widget).
 *
 * Static routes (with label, no paramKey):
 *   Track fixed pages like Growth Ideas or A/B Testing.
 */
const TRACKED_ROUTES: Record<
  string,
  {
    paramKey?: string
    typeLabel: string
    label?: string
    icon: any
    resolve?: (id: string) => string | undefined
  }
> = {
  [ROUTES.OFFERFUNNEL]: {
    paramKey: 'offerFunnelId',
    typeLabel: 'Flow',
    icon: shellIcons.flows,
    resolve: (id) => useFlowStore().getById(id)?.name
  },
  [ROUTES.WIDGET]: {
    paramKey: 'widgetId',
    typeLabel: 'Widget',
    icon: shellIcons.widgets,
    resolve: (id) => useWidgetStore().getById(id)?.name
  },
  [ROUTES.CAMPAIGN]: {
    paramKey: 'campaignId',
    typeLabel: 'Campaign',
    icon: shellIcons.campaigns,
    resolve: (id) => useCampaignStore().getById(id)?.name
  },
  [ROUTES.CAMPAIGN_EDITOR]: {
    paramKey: 'campaignId',
    typeLabel: 'Campaign',
    icon: shellIcons.campaigns,
    resolve: (id) => useCampaignStore().getById(id)?.name
  },
  [ROUTES.QUESTION]: {
    paramKey: 'questionId',
    typeLabel: 'Question',
    icon: shellIcons.questions,
    resolve: (id) => {
      const questionId = resolveQuestionId(useConfigStore().config, id)
      const question = questionId ? useQuestionStore().getById(questionId) : undefined
      if (!question) {
        return undefined
      }
      return getQuestionName(
        question,
        question.dimensionId ? useSegmentGroupStore().getById(question.dimensionId) : null
      )
    }
  },
  [ROUTES.SEGMENTGROUP]: {
    paramKey: 'segmentGroupId',
    typeLabel: 'Segment Group',
    icon: shellIcons.segments,
    resolve: (id) => useSegmentGroupStore().getById(id)?.name
  },
  [ROUTES.OFFER]: {
    paramKey: 'offerId',
    typeLabel: 'Offer',
    icon: shellIcons.offers,
    resolve: (id) => useOfferStore().getById(id)?.name
  },
  [ROUTES.FORM]: {
    paramKey: 'offerId',
    typeLabel: 'Form',
    icon: shellIcons.forms,
    resolve: (id) => useOfferStore().getById(id)?.name
  },
  [ROUTES.EMAIL]: {
    paramKey: 'emailId',
    typeLabel: 'Email',
    icon: shellIcons.emails,
    resolve: (id) => usePersonalizedEmailStore().getById(id)?.name
  },
  [ROUTES.TEMPLATE]: {
    paramKey: 'templateId',
    typeLabel: 'Template',
    icon: RectangleStackIcon,
    resolve: (id) => useTemplateStore().getById(id)?.title
  },
  [ROUTES.PAGE_EDITOR]: {
    paramKey: 'pageId',
    typeLabel: 'Page',
    icon: shellIcons.pages,
    resolve: (id) => pageName(id)
  },
  [ROUTES.GROWTH_IDEAS]: {
    typeLabel: 'Optimize',
    label: 'Growth Ideas',
    icon: shellIcons.ideas
  },
  [ROUTES.OPTIMIZATIONS]: {
    typeLabel: 'Optimize',
    label: 'Optimizations',
    icon: shellIcons.optimizations
  },
  [ROUTES.SPLIT_TESTS]: {
    typeLabel: 'Optimize',
    label: 'A/B Testing',
    icon: shellIcons.abTesting
  }
}

const MAX_ENTRIES = 5

function getStorageKey(teamPid: string): string {
  return `rm_recent_pages_${teamPid}`
}

function loadFromStorage(teamPid: string): RecentPage[] {
  try {
    const raw = localStorage.getItem(getStorageKey(teamPid))
    const pages: RecentPage[] = raw ? JSON.parse(raw) : []
    // Question pages were keyed by dimension ID before questions got their own
    // route parameter. The question page resolves a dim_ value to its question.
    return pages.map((page) => {
      const { segmentGroupId, ...params } = page.params
      return page.routeName === ROUTES.QUESTION && segmentGroupId !== undefined && params.questionId === undefined
        ? { ...page, params: { ...params, questionId: segmentGroupId } }
        : page
    })
  } catch {
    return []
  }
}

function saveToStorage(teamPid: string, pages: RecentPage[]): void {
  localStorage.setItem(getStorageKey(teamPid), JSON.stringify(pages))
}

const recentPages = ref<RecentPage[]>([])
const currentPage = ref<RecentPage | null>(null)
let activeTeamPid = ''

// A deep link or reload runs the router hook before the item stores have
// loaded, so the visit waits here until its name resolves. A later
// navigation replaces it.
const unnamedVisit = shallowRef<RouteLocationNormalized | null>(null)
watch(
  () => (unnamedVisit.value ? getTrackedRouteLabel(unnamedVisit.value) : undefined),
  (label) => {
    const to = unnamedVisit.value
    if (label && to) {
      recordVisit(to)
    }
  }
)

// Renaming the open item renames its entry too, so Recent pages and the TopBar agree.
watch(
  () => {
    const current = currentPage.value
    return current ? getTrackedRouteLabel({ name: current.routeName, params: current.params }) : undefined
  },
  (label) => {
    const current = currentPage.value
    if (!label || !current || label === current.label) {
      return
    }
    const renamed = { ...current, label }
    currentPage.value = renamed
    recentPages.value = recentPages.value.map((page) => (page === current ? renamed : page))
    saveToStorage(activeTeamPid, recentPages.value)
  }
)

function init(teamPid: string) {
  if (teamPid === activeTeamPid) {
    return
  }
  activeTeamPid = teamPid
  recentPages.value = loadFromStorage(teamPid)
  currentPage.value = recentPages.value[0] || null
}

// Older question entries were keyed by dimension ID. Once the team's config is
// loaded, rewrite them to the canonical question ID and keep only the newest
// entry per question, so the dim_ and raq_ forms don't take two slots.
function canonicalizeQuestionPages(teamPid: string) {
  const configStore = useConfigStore()
  if (!configStore.config || String(configStore.currentTeam?.pid) !== teamPid) {
    return
  }
  const seen = new Set<string>()
  const pages: RecentPage[] = []
  for (const page of recentPages.value) {
    if (page.routeName !== ROUTES.QUESTION) {
      pages.push(page)
      continue
    }
    const questionId = resolveQuestionId(configStore.config, page.params.questionId)
    if (!questionId || seen.has(questionId)) {
      continue
    }
    seen.add(questionId)
    pages.push(questionId === page.params.questionId ? page : { ...page, params: { ...page.params, questionId } })
  }
  if (pages.length !== recentPages.value.length || pages.some((page, index) => page !== recentPages.value[index])) {
    recentPages.value = pages
    saveToStorage(teamPid, pages)
  }
}

function recordVisit(to: RouteLocationNormalized) {
  unnamedVisit.value = null
  const teamPid = to.params.teamPid as string
  if (teamPid) {
    init(teamPid)
    canonicalizeQuestionPages(teamPid)
  }

  const routeName = to.name as string
  const config = TRACKED_ROUTES[routeName]
  if (!config) {
    currentPage.value = null
    return
  }

  if (!teamPid) {
    return
  }

  const label = getTrackedRouteLabel(to)
  if (!label) {
    unnamedVisit.value = to
    return
  }

  const params: Record<string, string> = { teamPid }
  const resourceId = config.paramKey ? to.params[config.paramKey] : undefined
  if (config.paramKey && typeof resourceId === 'string') {
    params[config.paramKey] = resourceId
  }

  const entry: RecentPage = {
    routeName,
    params,
    label,
    typeLabel: config.typeLabel,
    timestamp: Date.now()
  }

  currentPage.value = entry

  // Drop the older entry for the same page: by resource ID for detail routes, by route name for static ones
  const filtered = recentPages.value.filter((p) => {
    if (p.routeName !== routeName) {
      return true
    }
    if (config.paramKey) {
      return p.params[config.paramKey] !== params[config.paramKey]
    }
    return false
  })

  recentPages.value = [entry, ...filtered].slice(0, MAX_ENTRIES)
  saveToStorage(teamPid, recentPages.value)
}

export function useRecentPages() {
  return {
    recentPages: readonly(recentPages),
    currentPage: readonly(currentPage),
    init,
    recordVisit
  }
}
